import { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiCalendar } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import SearchBar from 'components/shared/SearchBar';
import AddNewUserModal from './AddNewUserModal';

interface DropdownOption {
  id: string | number;
  label: string;
}

interface UsersFiltersProps {
  setFilters: (filters: any) => void;
  filters: {
    organization: string;
    status: string;
    paymentStatus: string;
    paymentMethod: string;
    startDate: Date | null;
    endDate: Date | null;
    search: string;
  };
  users: any[];
  type?: string;
}

interface FilterState {
  organization: string;
  status: string;
  paymentStatus: string;
  paymentMethod: string;
  subscriptionType: string;
  startDate: Date | null;
  endDate: Date | null;
  search: string;
  [key: string]: string | Date | null;
}

export default function UsersFilters({ setFilters, filters, users, type = "superadmin" }: UsersFiltersProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showMapNewUserModal, setShowMapNewUserModal] = useState(false); // New state

  // Handle adding a new user
  const handleAddNewUser = (user: any) => {
    console.log('Adding new user:', user);
    // Here you would typically make an API call to add the user
    // For now, we'll just log the action
  };

  const [organizations, setOrganizations] = useState<string[]>([]);
  const userTypes: DropdownOption[] = [
    { id: '1', label: 'Super Admin' },
    { id: '2', label: 'Billing Admin' },
    { id: '3', label: 'Customer' },
    { id: '4', label: 'Support Agent' },
    { id: '5', label: 'Developer' }
  ];

  useEffect(() => {
    setFilters((prev: FilterState) => ({
      ...prev,
      search: searchTerm
    }));
  }, [searchTerm]);


  useEffect(() => {
    if (users.length > 0) {
      const organizations = users.map((user) => user.orgName);
      const uniqueOrganizations = [...new Set(organizations)];
      setOrganizations(uniqueOrganizations);
    }
  }, [users]);  

  const handleDropdownClick = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const handleOptionSelect = (dropdownName: string, value: string) => {
    setFilters((prev: FilterState) => ({ ...prev, [dropdownName]: value }));
    setOpenDropdown(null);
  };

  const clearFilters = () => {
    setFilters({
      organization: '',
      status: '',
      paymentStatus: '',
      paymentMethod: '',
      subscriptionType: '',
      startDate: null,
      endDate: null,
      search: ''
    });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters((prev: any) => ({
      ...prev,
      search: value
    }));
  };

  const Dropdown = ({ name, options, label }: { name: string; options: DropdownOption[]; label: string }) => (
    <div className="relative">
      <button
        onClick={() => handleDropdownClick(name)}
        className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
        style={{
          minWidth: '140px',
          maxWidth: '140px',
        }}
      >
        <span className="truncate mr-2">
          {filters[name as keyof typeof filters]?.toString() || label}
        </span>
        {openDropdown === name ? <FiChevronUp className="flex-shrink-0" /> : <FiChevronDown className="flex-shrink-0" />}
      </button>

      {openDropdown === name && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpenDropdown(null)}
          />
          <div className="absolute top-full left-0 mt-2 w-48 bg-[#2A2A2A] rounded-md shadow-lg z-20 py-1 border border-white/[0.08]">
            <button
              disabled
              className="w-full text-left px-4 py-2 text-gray-500 bg-[#2A2A2A] cursor-default truncate opacity-50"
            >
              {label}
            </button>
            
            <div className="border-b border-white/[0.08] my-1"></div>
            
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(name, option.label)}
                className="w-full text-left px-4 py-2 text-gray-400 hover:bg-[#3a3a3a33] hover:text-white truncate"
                title={option.label}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="flex justify-between items-center mb-4">
      <div className="flex items-center gap-3">
        <SearchBar
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search users..."
        />
        
        <div className="relative">
          <button
            onClick={() => setShowDatePicker(!showDatePicker)}
            className="flex items-center justify-between bg-[#2A2A2A] rounded-md px-3 py-2 text-gray-400 hover:bg-[#3a3a3a33]"
            style={{
              minWidth: '140px',
              maxWidth: '200px',
            }}
          >
            <div className="flex items-center gap-2 truncate">
              <FiCalendar className="flex-shrink-0" />
              <span className="truncate">
                {(filters.startDate && filters.endDate)
                  ? `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
                  : 'Select Duration'
                }
              </span>
            </div>
          </button>
          
          {showDatePicker && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowDatePicker(false)}
              />
              <div className="absolute top-full left-0 mt-2 z-20 bg-[#2A2A2A] rounded-lg p-4 border border-white/[0.08]">
                <DatePicker
                  selectsRange={true}
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onChange={(update: [Date | null, Date | null]) => {
                    setFilters((prev: any) => ({
                      ...prev,
                      startDate: update[0],
                      endDate: update[1]
                    }));
                    if (update[0] && update[1]) {
                      setShowDatePicker(false);
                    }
                  }}
                  inline
                />
              </div>
            </>
          )}
        </div>
        
        <Dropdown
          name="userType"
          options={userTypes}
          label="User Type"
        />
        
    

        {
          type === "superadmin" &&
          <Dropdown
          name="organization"
          options={organizations.map((organization) => ({ id: organization, label: organization }))}
          label="Organization"
        />}

        {Object.values(filters).some(Boolean) && (
          <button 
            onClick={clearFilters}
            className="text-red-400 hover:text-red-300"
          >
            Clear all
          </button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <button className="flex items-center gap-2 bg-[#2A2A2A] rounded-md px-4 py-2 text-white">
          ↓ Export
        </button>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 rounded-md px-4 py-2 text-white" onClick={() => setShowMapNewUserModal(true)}>
          + Add User
        </button>
      </div>

      {/* Add New User Modal */}
      <AddNewUserModal
        isOpen={showMapNewUserModal}
        onClose={() => setShowMapNewUserModal(false)}
        onAddUser={handleAddNewUser}
      />
    </div>
  );
} 
